import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { App, Button, Form, Input, Typography } from "antd"
import Col from "antd/es/grid/col"
import Row from "antd/es/grid/row"
import TextArea from "antd/es/input/TextArea"
import {
  type InternalUploadFile,
  type UploadChangeParam,
} from "antd/es/upload/interface"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useState } from "react"

import {
  classAtoms,
  classSessionAtoms,
  studentAtoms,
  tryoutAtoms,
} from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import CheckboxContentBlock from "#blocks/Form/CheckboxContentBlock"
import { SFormHeader } from "#blocks/Form/formComponents"
import Uploader from "#blocks/Uploader"
import Firebase from "#classes/Firebase"
import { Student } from "#classes/Student"
import { CLASS_GROUPS } from "#constants/classOptions"
import { CLASS_GROUP_TO_SKILL_TYPE } from "#constants/skillLevelConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  UploadFile,
  UploadStatus,
} from "#customTypes/composedTypes/firestoreTypes"
import { StudentSkillType } from "#customTypes/composedTypes/studentTypes"
import {
  STEP_2_SKILL_FIELD_BY_TYPE,
  TryoutConstructor,
} from "#customTypes/composedTypes/tryoutTypes"
import { GenericObject } from "#customTypes/types"
import SkillLevelDropdown from "#forms/SkillLevelDropdown"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { STANDARD_FILE_DATE_FORMAT } from "#utils/dateHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"
import { ErrorType, formatError } from "#utils/textFormatters"

const STEP_2_TEXT = {
  scanEval: {
    title: "Scan eval form",
    description: "Make sure to get eval form back from the teacher and scan.",
  },
  updateLevel: {
    title: "Update level",
    description: "Check level from the eval form.",
  },
  passedTryout: {
    title: "Tryout result",
    description: "Did the student pass the tryout evaluation? ",
  },
}

const TryoutProgressStep2 = () => {
  const form = Form.useFormInstance()
  const studentId = Form.useWatch("studentId", form)
  const tryoutId = Form.useWatch("id", form)
  const didPassTryout = Form.useWatch(["step2", "didPassTryout"], form) as
    | boolean
    | undefined

  // Watch skill level fields for auto-check
  const coreSkillLevel = Form.useWatch(["step2", "coreSkillLevel"], form)
  const digitalSkillLevel = Form.useWatch(["step2", "digitalSkillLevel"], form)
  const sculptureSkillLevel = Form.useWatch(
    ["step2", "sculptureSkillLevel"],
    form
  )

  const { message } = App.useApp()

  // Track uploading file state to show progress during upload
  const [uploadingFile, setUploadingFile] = useState<InternalUploadFile | null>(
    null
  )

  const tryout = useOneDoc<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    tryoutId
  )

  const student = useOneDoc(studentAtoms, DOC_TYPES.STUDENTS, studentId)

  const session = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    tryout?.sessionId
  )

  // Get the class to determine the class group
  const tryoutClass = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    session?.classId
  )

  // Determine skill type based on class group
  const skillType = useMemo(() => {
    if (!tryoutClass?.group) return StudentSkillType.core
    return CLASS_GROUP_TO_SKILL_TYPE[tryoutClass.group] ?? StudentSkillType.core
  }, [tryoutClass?.group])

  const skillFieldName = STEP_2_SKILL_FIELD_BY_TYPE[skillType]

  // Derive uploadedEval file from tryout (for persistence across navigation)
  // Use uploadingFile when in progress, otherwise fall back to tryout data
  const evalFile = tryout?.step2?.uploadedEval
  const uploadedEvalFile: InternalUploadFile[] = uploadingFile
    ? [uploadingFile]
    : evalFile
      ? [
          {
            uid: evalFile.uid,
            name: evalFile.name,
            status: evalFile.status,
            url: evalFile.url,
            percent: evalFile.percent ?? 100,
          } as InternalUploadFile,
        ]
      : []

  // Auto-check "Level updated" when skill level is selected
  useEffect(() => {
    if (coreSkillLevel || digitalSkillLevel || sculptureSkillLevel) {
      form.setFieldValue(["step2", "isLevelUpdated"], true)
    }
  }, [coreSkillLevel, digitalSkillLevel, sculptureSkillLevel, form])
  const startDate = dayjs(session?.eventStart).format(STANDARD_FILE_DATE_FORMAT)
  const prettyFilename = `${student ? new Student(student).getNameAndDob() : "Unknown student"} - tryout eval - ${startDate}`
  const filename = `${startDate} tryoutEval ${tryoutId}`
  const metadata = {
    studentId,
    tryoutId,
    // This name is used when downloading the file from the browser
    contentDisposition: `inline; filename="${prettyFilename}"`,
  }

  // One batch, so the tryout's progress flags and the student's files/skill
  // can't end up disagreeing when one of the two writes fails.
  const saveTryoutAndStudent = useCallback(
    async (
      tryoutPatch: GenericObject,
      studentPatch: GenericObject
    ): Promise<boolean> => {
      try {
        await Firebase.getInstance().updateDocuments({
          [DOC_TYPES.TRYOUTS]: { [tryoutId]: tryoutPatch },
          [DOC_TYPES.STUDENTS]: { [studentId]: studentPatch },
        })
        return true
      } catch (error) {
        message.error(
          `Failed to save tryout progress: ${formatError(error as ErrorType)}`
        )
        return false
      }
    },
    [tryoutId, studentId, message]
  )

  const handleUploadStatusChange = (info: UploadChangeParam) => {
    const { status } = info.file

    if (status === UploadStatus.UPLOADING) {
      setUploadingFile({
        uid: info.file.uid,
        name: prettyFilename,
        status: UploadStatus.UPLOADING,
        percent: info.file.percent ?? 0,
      } as InternalUploadFile)
    } else if (status === UploadStatus.DONE) {
      setUploadingFile(null)

      const result: UploadFile = {
        url: info.file.response,
        name: prettyFilename,
        size: info.file.size,
        status: UploadStatus.DONE,
        uid: info.file.uid,
        percent: info.file.percent ?? 100,
      }

      form.setFieldValue(["step2", "isEvalScanned"], true)
      form.setFieldValue(["step2", "uploadedEval"], result)

      void saveTryoutAndStudent(
        {
          "step2.uploadedEval": result,
          "step2.isEvalScanned": true,
        },
        {
          [`files.tryoutEvals.${tryoutId}`]: {
            name: prettyFilename,
            url: result.url,
            size: result.size,
            tryoutId,
          },
        }
      ).then((saved) => {
        if (saved) {
          message.success(`File ${info.file.name} uploaded successfully.`)
        }
      })
    } else if (status === UploadStatus.ERROR) {
      setUploadingFile(null)
      message.error(`File ${info.file.name} upload failed.`)
    }
  }

  const handleDeleteUploadedFile = () => {
    form.setFieldValue(["step2", "uploadedEval"], null)
    form.setFieldValue(["step2", "isEvalScanned"], false)

    void saveTryoutAndStudent(
      {
        "step2.uploadedEval": null,
        "step2.isEvalScanned": false,
      },
      { [`files.tryoutEvals.${tryoutId}`]: null }
    )
  }

  const handleDidPassTryoutChange = useCallback(
    (newValue: boolean | undefined) => {
      form.setFieldValue(["step2", "didPassTryout"], newValue)

      if (typeof newValue !== "boolean") return

      Firebase.getInstance().updateDocument(DOC_TYPES.TRYOUTS, tryoutId, {
        "step2.didPassTryout": newValue,
      })
    },
    [form, tryoutId]
  )

  const handleSkillLevelChange = useCallback(
    (newSkillLevel: string | string[]) => {
      if (typeof newSkillLevel !== "string") return
      if (didPassTryout !== true) return
      if (!tryoutId || !studentId) return

      form.setFieldValue(["step2", "isLevelUpdated"], true)

      void saveTryoutAndStudent(
        {
          [`step2.${skillFieldName}`]: newSkillLevel,
          "step2.isLevelUpdated": true,
        },
        {
          [skillFieldName]: newSkillLevel,
          [`skillHistory.${getCurrentSemester()}.${skillType}`]: newSkillLevel,
        }
      )
    },
    [
      didPassTryout,
      tryoutId,
      studentId,
      form,
      skillType,
      skillFieldName,
      saveTryoutAndStudent,
    ]
  )

  return (
    <Flex col style={{ padding: "20px 30px", width: "100%" }} gap={30}>
      {/* Scan Eval */}
      <Row gutter={48}>
        <Col span={12}>
          <CheckboxContentBlock
            fieldName={["step2", "isEvalScanned"]}
            title={STEP_2_TEXT.scanEval.title}
            description={STEP_2_TEXT.scanEval.description}
          >
            <Uploader
              fileList={uploadedEvalFile}
              onStatusChange={handleUploadStatusChange}
              onDelete={handleDeleteUploadedFile}
              folder={`${studentId}/files/tryoutEvals`}
              filename={filename}
              metadata={metadata}
            />
          </CheckboxContentBlock>
        </Col>
      </Row>

      {/* Tryout Result */}
      <Row gutter={48}>
        <Col span={24}>
          <SFormHeader>{STEP_2_TEXT.passedTryout.title}</SFormHeader>
          <Typography.Text type="secondary">
            {STEP_2_TEXT.passedTryout.description}
          </Typography.Text>

          <Flex gap={16} style={{ marginTop: 16, alignItems: "center" }}>
            <Form.Item
              name={["step2", "didPassTryout"]}
              style={{ marginBottom: 0, display: "none" }}
            >
              <Input type="hidden" />
            </Form.Item>
            <Button
              type={didPassTryout === true ? "primary" : "default"}
              icon={<CheckOutlined />}
              onClick={() => {
                const newValue = didPassTryout === true ? undefined : true
                handleDidPassTryoutChange(newValue)
              }}
              style={{
                backgroundColor: didPassTryout === true ? "#52c41a" : undefined,
                borderColor: didPassTryout === true ? "#52c41a" : undefined,
              }}
            >
              Passed
            </Button>
            <Button
              type={didPassTryout === false ? "primary" : "default"}
              danger={didPassTryout === false}
              icon={<CloseOutlined />}
              onClick={() => {
                const newValue = didPassTryout === false ? undefined : false
                handleDidPassTryoutChange(newValue)
              }}
            >
              Failed
            </Button>
          </Flex>

          {/* Passed: Update level */}
          {didPassTryout === true && (
            <AnimateInFlex
              animation={ANIMATION_BY_KEY.leftSm}
              col
              gap={16}
              style={{ marginTop: 20, width: 400 }}
            >
              <CheckboxContentBlock
                fieldName={["step2", "isLevelUpdated"]}
                title={STEP_2_TEXT.updateLevel.title}
                description={STEP_2_TEXT.updateLevel.description}
              >
                <Flex style={{ marginTop: 20 }}>
                  <SkillLevelDropdown
                    fieldName={["step2", skillFieldName]}
                    classGroup={tryoutClass?.group ?? CLASS_GROUPS.CORE}
                    required
                    onChange={handleSkillLevelChange}
                  />
                </Flex>
              </CheckboxContentBlock>
            </AnimateInFlex>
          )}

          {/* Failed: Reason */}
          {didPassTryout === false && (
            <AnimateInFlex
              animation={ANIMATION_BY_KEY.leftSm}
              col
              gap={16}
              style={{ marginTop: 20 }}
            >
              <Typography.Text type="danger" strong>
                Please provide a reason for the student not passing the tryout.
                This is just for our records.
              </Typography.Text>
              <Form.Item
                name={["step2", "failureReason"]}
                label="Reason"
                rules={[
                  {
                    required: didPassTryout === false,
                    message: "Please provide a reason for not passing",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Explain why the student did not pass the tryout evaluation..."
                />
              </Form.Item>
            </AnimateInFlex>
          )}
        </Col>
      </Row>

      {/* Hidden fields */}
      <Form.Item name={["step2", "uploadedEval"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
    </Flex>
  )
}

export default TryoutProgressStep2
